import { useEffect, useState } from 'react';
import {
  Form, Input, Select, InputNumber, Row, Col, Switch, Typography, Divider, DatePicker, Alert
} from 'antd';
import { studioLookups } from '../../itemsApi';
import {
  TAXABILITY_TYPES, PRICING_METHODS, humaniseEnum
} from '../../studioConstants';
import TripleSalesRateInput from '../popups/TripleSalesRateInput';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Step 3 — UOM, GST & Pricing.
//
// Goods/service split lives in Step 2 (is_goods / is_service); this
// step focuses on tax classification (HSN/SAC, GST rate, taxability)
// and base pricing. Operational fields (lead time, min order qty,
// reorder levels) are also captured here so the entire commercial
// envelope is in one step.
// ─────────────────────────────────────────────────────────────────

export default function GstPricingStep({ disabled }) {
  const form = Form.useFormInstance();
  const [uoms, setUoms] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await studioLookups.uoms({ q: '', limit: 200 });
        if (!cancelled) setUoms(Array.isArray(rows) ? rows : []);
      } catch (_e) { /* swallow */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div>
      <h2 className="erp-studio-section-title">UOM, GST & pricing</h2>
      <p className="erp-studio-section-help">
        GST and HSN/SAC are stored on the core item and used later in invoices. Pricing here is
        the default — quotation, contract or counter-sale rates can override it.
      </p>

      <Text strong style={{ fontSize: 13 }}>Unit of measurement</Text>
      <Row gutter={[16, 0]} style={{ marginTop: 8 }}>
        <Col xs={24} md={10}>
          <Form.Item label="Default UOM" name="defaultUomId">
            <Select
              disabled={disabled}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Pick from UOM Master"
              options={uoms.map((u) => ({
                value: u.id,
                label: `${u.uomCode || u.uom_code} — ${u.uomName || u.uom_name}`
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="Default UQC"
            name="defaultUqcCode"
            normalize={(v) => (v === undefined || v === null || v === '') ? v : String(v).toUpperCase()}
            rules={[
              { len: 3, message: 'UQC is exactly 3 characters.' },
              { pattern: /^[A-Z0-9]{3}$/, message: 'A–Z, 0–9 only.' }
            ]}
            tooltip="GSTN UQC code — auto-filled if the UOM has one."
          >
            <Input placeholder="e.g. KGS" disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item
            label="Decimal precision"
            name="decimalPrecision"
            tooltip="Digits after the decimal point in qty / area / volume."
          >
            <InputNumber min={0} max={6} disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>

      <Divider className="erp-studio-divider" />
      <Text strong style={{ fontSize: 13 }}>GST classification</Text>

      <Alert
        type="info" showIcon
        style={{ margin: '8px 0', fontSize: 12 }}
        message="Use Step 2 to mark this item as a Good or a Service. Taxability here only covers GST exposure (Taxable / Exempt / Nil rated / Non-GST / Zero rated). RCM is not handled on Item Master."
      />

      <Row gutter={[16, 0]}>
        <Col xs={24} md={8}>
          <Form.Item
            label="HSN / SAC"
            name="hsncode"
            rules={[
              { max: 20, message: 'Max 20 characters.' },
              { pattern: /^[A-Z0-9]*$/i, message: 'Alphanumeric only.' }
            ]}
          >
            <Input placeholder="e.g. 70051010" disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="GST rate (%)" name="gstRatePercent">
            <InputNumber min={0} max={99.99} step={0.5} precision={2} disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="Taxability"
            name="taxabilityType"
            rules={[{
              validator: (_, v) => {
                if (v === 'service' || v === 'rcm') {
                  return Promise.reject(
                    new Error('"service" and "rcm" are not allowed on Item Master — use Step 2 flags.')
                  );
                }
                return Promise.resolve();
              }
            }]}
          >
            <Select
              disabled={disabled}
              options={TAXABILITY_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col xs={24} md={8}>
          <Form.Item label="Cess applicable" name="cessApplicable" valuePropName="checked">
            <Switch disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Cess rate (%)" name="cessRate">
            <InputNumber min={0} max={99.99} step={0.5} precision={2} disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>

      <Divider className="erp-studio-divider" />
      <Text strong style={{ fontSize: 13 }}>Pricing</Text>

      <Row gutter={[16, 0]} style={{ marginTop: 8 }}>
        <Col xs={24} md={8}>
          <Form.Item label="Pricing method" name="pricingMethod">
            <Select
              disabled={disabled}
              allowClear
              options={PRICING_METHODS.map((s) => ({ value: s, label: humaniseEnum(s) }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Purchase rate" name="purchaseRate">
            <InputNumber min={0} step={0.01} precision={4} disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="MRP" name="mrp">
            <InputNumber min={0} step={0.01} precision={4} disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col xs={24} md={16}>
          {/* Std / Min / Max selling price in the Default UOM, with a live
              SQF⇄SQM equivalence hint. The proforma converts the Std rate
              into the document's Price UOM when the item is picked. */}
          <Form.Item label="Sales rate — Std / Min / Max (in Default UOM)" name="salesRate">
            <TripleSalesRateInput form={form} disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Rate effective from" name="rateEffectiveFrom">
            <DatePicker style={{ width: '100%' }} disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>

      <Divider className="erp-studio-divider" />
      <Text strong style={{ fontSize: 13 }}>Operational</Text>

      <Row gutter={[16, 0]} style={{ marginTop: 8 }}>
        <Col xs={24} md={6}>
          <Form.Item label="Min order qty" name="minOrderQty">
            <InputNumber min={0} step={1} disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Lead time (days)" name="leadTimeDays">
            <InputNumber min={0} step={1} precision={0} disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Reorder level" name="reorderLevel">
            <InputNumber min={0} step={1} disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Reorder qty" name="reorderQty">
            <InputNumber min={0} step={1} disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <Form.Item label="Weight per unit (kg)" name="weightPerUnit">
            <InputNumber min={0} step={0.001} precision={4} disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Gross weight (kg)" name="grossWeight">
            <InputNumber min={0} step={0.001} precision={4} disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
}
